import React, { useMemo, ReactNode } from "react";
import { Avatar, Button, Dialog, PlainButton } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import type { ContactInfoData } from "@jugl-web/domain-resources/contacts/utils";
import { ContactIntelligenceInfoSection } from "../ContactIntelligenceInfoSection";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as MingcuteIcon } from "./assets/mingcute.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as PricingIcon } from "./assets/pricing.svg";
import { ReactComponent as Shadow } from "./assets/shadow.svg";
import platformsImage from "./assets/platforms.png";

export const VisitorsIntelligenceDialog: React.FC<{
  isOpen: boolean;
  onRequestClose: () => void;
}> = ({ isOpen, onRequestClose }) => {
  const { t } = useTranslations();
  const { navigateToPage } = useNavigation();

  const whatYouGetItems = useMemo(
    () => [
      {
        title: t({
          id: "contacts-page.track-chat-visitors",
          defaultMessage: "Track Website chat Visitors",
        }),
        description: t({
          id: "contacts-page.track-chat-visitors-desc",
          defaultMessage:
            "Get the names, LinkedIn profiles, and email addresses of real people on your site 🔍",
        }),
      },
      {
        title: t({
          id: "contacts-page.identify-companies",
          defaultMessage: "Identify Companies behind",
        }),
        description: t(
          {
            id: "contacts-page.identify-companies-desc",
            defaultMessage:
              "Get details like company name, industry, size, revenue, and social profiles - <highlightedText>no manual input needed</highlightedText>",
          },
          {
            highlightedText: (text: ReactNode) => (
              <span className="bg-linear-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
                {text}
              </span>
            ),
          }
        ),
      },
      {
        title: t({
          id: "contacts-page.turn-insights",
          defaultMessage: "Turn Insights into Opportunities",
        }),
        description: t({
          id: "contacts-page.turn-insights-desc",
          defaultMessage:
            "Use this data to personalise outreach, qualify leads, and close more deals 📈",
        }),
      },
      {
        title: t({
          id: "contacts-page.upgrade-business-plan-title",
          defaultMessage: "Upgrade to Business Plan to Identify new Customers",
        }),
        description: t({
          id: "contacts-page.upgrade-business-plan-desc",
          defaultMessage:
            "Feature available only for the Business Plan. Visitors Intelligence applies to all new visitors after plan switching",
        }),
      },
    ],
    [t]
  );
  const visitorIntelligenceExampleData = useMemo<ContactInfoData>(
    () => ({
      profile: {},
      visitorIntelligence: {
        phoneNumber: {
          type: "plain",
          value: "+91 1234 567 890",
        },
        personalEmail: { type: "plain", value: "marcus.chen@bigbrothers.com" },
        businessEmail: { type: "notApplicable" },
        company: { type: "plain", value: "Big Brothers" },
        title: { type: "plain", value: "Executive Director" },
        location: { type: "plain", value: "Dallas, TX" },
        linkedin: { type: "link", value: "See profile" },
        revenue: { type: "plain", value: "$5M" },
        industry: { type: "plain", value: "Health and Pharmaceuticals" },
        website: { type: "link", value: "https://www.website.com" },
        companyLinkedin: { type: "link", value: "See profile" },
        confidence: 9,
      },
    }),
    []
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onRequestClose}
      isFullscreenOnMobile
      className="flex w-full max-w-[1000px] flex-col overflow-hidden"
    >
      <div className="flex h-[60px] shrink-0 items-center justify-between gap-4 border-0 border-b border-solid border-dark-100 px-4 md:gap-6 md:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <MingcuteIcon className="shrink-0" />
          <span className="truncate font-secondary font-semibold text-[#383838]">
            {t({
              id: "contacts-page.visitors-intelligence",
              defaultMessage: "Visitors Intelligence",
            })}
          </span>
        </div>
        <PlainButton className="shrink-0" onClick={onRequestClose}>
          <CloseIcon />
        </PlainButton>
      </div>

      <div className="jugl__custom-scrollbar flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4 md:gap-10 md:p-8">
        <div className="relative flex shrink-0 flex-col gap-6 overflow-hidden rounded-2xl bg-[#080410] p-5 md:gap-10 md:rounded-3xl md:p-8">
          <Shadow className="pointer-events-none absolute top-0 left-0" />
          <img
            src={platformsImage}
            alt=""
            className="pointer-events-none absolute right-0 bottom-0 h-full max-md:max-w-[45%] md:right-[60px]"
          />
          <span className="z-10 font-secondary text-xl leading-[150%] font-semibold text-white md:text-3xl">
            {t(
              {
                id: "contacts-page.identify-traffic",
                defaultMessage: "Identify 70-80%{br} of your website traffic",
              },
              { br: <br /> }
            )}
          </span>
          <Button
            variant="secondary"
            iconEnd={<ArrowIcon />}
            className="z-10 w-max"
            onClick={() => navigateToPage("cxWorkspacePlans")}
          >
            {t({
              id: "contacts-page.upgrade-to-business",
              defaultMessage: "Upgrade to Business",
            })}
          </Button>
        </div>

        <div className="flex min-w-0 shrink-0 flex-col gap-6 md:gap-10 md:px-8">
          <div className="flex flex-col gap-4 md:gap-6">
            <span className="font-secondary text-lg font-semibold text-[#1A2023] md:text-[22px]">
              {t({
                id: "contacts-page.what-you-get",
                defaultMessage: "What you get?",
              })}
            </span>
            <div>
              {whatYouGetItems.map((item, index) => (
                <div key={item.title}>
                  <div className="flex flex-col gap-1 py-1.5 md:py-2">
                    <span className="font-secondary text-[15px] font-medium text-dark">
                      {item.title}
                    </span>
                    <span className="font-secondary text-sm leading-[160%] text-dark-700">
                      {item.description}
                    </span>
                  </div>
                  {index < whatYouGetItems.length - 1 && (
                    <div className="my-1.5 h-px w-full rounded-[1px] bg-[#F2F2F2] md:my-2.5" />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex min-w-0 shrink-0 flex-col gap-2">
            <span className="font-secondary text-sm text-dark-600">
              {t({
                id: "contacts-page.visitor-intelligence-example",
                defaultMessage: "✨ Visitor Intelligence profile example:",
              })}
            </span>
            <div className="flex min-w-0 flex-col gap-4 rounded-[14px] bg-grey-100 p-3 md:gap-[21px] md:p-6 md:pt-4">
              <div className="flex items-center gap-4 px-2">
                <Avatar username="Marcus Chen" size="md" />
                <span className="font-secondary text-[17px] leading-[140%] font-medium text-dark">
                  Marcus Chen
                </span>
              </div>
              <div className="jugl__custom-scrollbar jugl__custom-scrollbar-horizontal min-w-0 overflow-x-auto">
                <div className="min-w-[760px]">
                  <ContactIntelligenceInfoSection
                    sectionId="visitorIntelligence"
                    variant="preview"
                    values={visitorIntelligenceExampleData.visitorIntelligence}
                  />
                </div>
              </div>
            </div>
          </div>

          <Button
            variant="secondary"
            className="w-full md:w-max"
            iconEnd={<PricingIcon />}
            onClick={() => navigateToPage("cxWorkspacePlans")}
          >
            {t({
              id: "contacts-page.get-business-plan",
              defaultMessage: "Get Business Plan",
            })}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
